// Team member permission utilities

export const getPermissions = () => {
  try {
    const selectedArtist = localStorage.getItem('selectedArtist');
    if (!selectedArtist) return null;
    
    const userData = JSON.parse(localStorage.getItem('dizeeUser') || '{}');
    const selectedArtistData = JSON.parse(selectedArtist);
    
    // For team members in delegated mode, return their permissions
    if (userData && ['manager', 'support'].includes(userData.professionRole)) {
      return selectedArtistData.permissions;
    }
    
    return null;
  } catch (error) {
    console.error('Error parsing permissions:', error);
    return null;
  }
};

export const getAccountType = () => {
  try {
    const selectedArtist = localStorage.getItem('selectedArtist');
    const userData = JSON.parse(localStorage.getItem('dizeeUser') || '{}');
    
    // If viewing own account, user is the artist/owner
    if (!selectedArtist) {
      if (userData.professionRole === 'artist') {
        return 'artist';
      }
      return null;
    }
    
    // If in delegated mode, check the user's profession role
    const userRole = userData.professionRole;
    if (userRole === 'manager' || userRole === 'support') {
      return userRole;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting account type:', error);
    return null;
  }
};

export const hasFinancialAccess = () => {
  try {
    const permissions = getPermissions();
    const accountType = getAccountType();
    
    // Artists always have financial access
    if (accountType === 'artist') {
      return true;
    }
    
    // Support never has financial access
    if (accountType === 'support') {
      return false;
    }
    
    // For managers, check the financialAccess flag
    if (accountType === 'manager' && permissions) {
      const selectedArtist = localStorage.getItem('selectedArtist');
      if (selectedArtist) {
        const selectedArtistData = JSON.parse(selectedArtist);
        return selectedArtistData.financialAccess !== false; // Default to true for backwards compatibility
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error checking financial access:', error);
    return false;
  }
};

export const canAccessFinancials = (appName) => {
  // Financial data only applies to specific apps
  const financialApps = ['bookings', 'analytics', 'tm'];
  
  if (!financialApps.includes(appName)) {
    return true; // Non-financial apps don't need this check
  }
  
  return hasFinancialAccess();
};

export const isDelegatedUser = () => {
  const permissions = getPermissions();
  return !!permissions;
};

export const canAccessApp = (appName) => {
  const permissions = getPermissions();
  if (!permissions) return true; // Owner has full access
  
  return permissions.apps?.includes(appName) || false;
};

export const canEditApp = (appName) => {
  const permissions = getPermissions();
  const accountType = getAccountType();
  
  if (!permissions && accountType === 'artist') return true; // Owner has full access
  
  // Support accounts can NEVER edit, only view
  if (accountType === 'support') {
    return false;
  }
  
  const hasAppAccess = permissions.apps?.includes(appName);
  const appPermission = permissions.appPermissions?.[appName];
  
  return hasAppAccess && appPermission === 'edit';
};

export const canViewApp = (appName) => {
  const permissions = getPermissions();
  if (!permissions) return true; // Owner has full access
  
  return permissions.apps?.includes(appName) || false;
};

// New helper functions for specific actions
export const canCreateContent = (appName) => {
  return canEditApp(appName);
};

export const canEditContent = (appName) => {
  return canEditApp(appName);
};

export const canDeleteContent = (appName) => {
  return canEditApp(appName);
};

export const hasAIAccess = () => {
  const accountType = getAccountType();
  
  // Support accounts NEVER have AI access
  if (accountType === 'support') {
    return false;
  }
  
  return canAccessApp('ai');
};

export const getAIUsageInfo = () => {
  const permissions = getPermissions();
  if (!permissions) {
    return { limit: -1, used: 0 }; // Unlimited for owner
  }
  
  return {
    limit: permissions.aiUsageLimit || 1000,
    used: permissions.aiUsageCount || 0
  };
};

export const getAllApps = () => {
  const permissions = getPermissions();
  if (!permissions) {
    // Owner sees all apps
    return ['workflows', 'connectors', 'catalog', 'bookings', 'vault', 'merch', 'analytics', 'ai', 'charting', 'playlisting', 'social-listening', 'spotify-revenue', 'pages', 'knowledge', 'notes', 'tasks', 'tourmngr'];
  }
  // Team member sees only apps they have access to
  return permissions.apps || [];
};

// Check if team member is in view-only mode for team management
export const canManageTeam = () => {
  const permissions = getPermissions();
  if (!permissions) return true; // Owner can manage
  
  return false; // Team members cannot manage team
}; 